import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import accountingClientsAPI from "../../api/accountingClientsAPI";
import useRecordLock from "../../hooks/useRecordLock";
import RecordLockBanner from "../../components/RecordLockBanner";
import useGuardedMutation from "../../hooks/useGuardedMutation";
import { useAuth } from "../../context/AuthContext";
import WhtProfileFields from "./WhtProfileFields";
import BranchCodeField, { isValidBranchCode, BRANCH_CODE_ERROR } from "./BranchCodeField";

const TABS = [
  { key: "profile", label: "Profile" },
  { key: "contact", label: "Contacts & Address" },
  { key: "tax", label: "Tax & Compliance" },
  { key: "audit", label: "Audit" },
];

const emptyForm = {
  industryTemplate: "general_service",
  clientType: "individual",
  name: "",
  tin: "",
  rdoCode: "",
  branchCode: "0000",
  whtProfile: {},
  contactInfo: { email: "", phone: "", mobile: "" },
  address: {
    unitFloor: "",
    building: "",
    street: "",
    barangay: "",
    cityMunicipality: "",
    province: "",
    zipCode: "",
  },
  taxObligations: {
    percentageTax: false,
    vat: false,
    withholdingTaxExpanded: false,
    withholdingTaxCompensation: false,
    exciseTax: false,
    incomeTax: false,
    withholdingFinal: false,
  },
  corporationDetails: { secRegistrationNumber: "", businessStyle: "", dateOfIncorporation: "" },
  individualDetails: { birthDate: "", birthPlace: "", citizenship: "", occupation: "" },
};

export default function AccountingClientDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isLocked, isOwner, lockedByName, lockedAt, releaseLock } = useRecordLock(id, "accountingClient");
  // Branch code + withholding tax profile are accounting-firm fields (WHT-1);
  // other tenant types (billing, mrwd) see the page as before.
  const { user } = useAuth();
  const isAccountingFirm = user?.tenantType === "accounting_firm";

  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState(emptyForm);
  const [originalData, setOriginalData] = useState(emptyForm);

  const [meta, setMeta] = useState({
    status: "active",           // active | archived
    canDelete: false,           // true if no transactions
    createdBy: "",
    createdAt: "",
    updatedBy: "",
    updatedAt: "",
  });

  const isDirty = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(originalData),
    [formData, originalData]
  );

  useEffect(() => {
    if (id) fetchClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchClient = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await accountingClientsAPI.getById(id);
      const client = res?.data?.client;

      const hydrated = {
        ...emptyForm,
        ...client,
        contactInfo: { ...emptyForm.contactInfo, ...(client?.contactInfo || {}) },
        address: { ...emptyForm.address, ...(client?.address || {}) },
        taxObligations: { ...emptyForm.taxObligations, ...(client?.taxObligations || {}) },
        corporationDetails: { ...emptyForm.corporationDetails, ...(client?.corporationDetails || {}) },
        individualDetails: { ...emptyForm.individualDetails, ...(client?.individualDetails || {}) },
        branchCode: client?.branchCode || "0000",
        // The stored (possibly partial) profile; WhtProfileFields resolves the
        // effective values (hasEmployees falls back to the obligation).
        whtProfile: client?.whtProfile || {},
      };

      setFormData(hydrated);
      setOriginalData(hydrated);

      // If your backend can return these, great. Otherwise you can compute canDelete via a separate endpoint.
      setMeta((m) => ({
        ...m,
        status: client?.status || "active",
        canDelete: !!client?.canDelete, // recommended backend flag
        createdBy: client?.createdByName || "",
        createdAt: client?.createdAt || "",
        updatedBy: client?.updatedByName || "",
        updatedAt: client?.updatedAt || "",
      }));
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load client");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (path, value) => {
    const keys = path.split(".");
    setFormData((prev) => {
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleTaxObligationChange = (key, checked) => {
    setFormData((prev) => ({
      ...prev,
      taxObligations: { ...prev.taxObligations, [key]: checked },
    }));
  };

  const validate = () => {
    const errors = [];
    if (!formData.name.trim()) errors.push("Client name is required");
    if (!formData.tin.trim()) errors.push("TIN is required");
    if (!formData.rdoCode.trim()) errors.push("RDO Code is required");
    if (isAccountingFirm && !isValidBranchCode(formData.branchCode)) errors.push(BRANCH_CODE_ERROR);

    // Only enforce SEC reg if clientType is corporation/opc/partnership (your call)
    if (["corporation", "opc", "partnership"].includes(formData.clientType)) {
      if (!formData.corporationDetails.secRegistrationNumber?.trim()) {
        errors.push("SEC Registration Number is required for this client type");
      }
    }
    return errors;
  };

  const handleSave = async () => {
    const errors = validate();
    if (errors.length) return setError(errors.join(", "));

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const payload = {
        ...formData,
        contactInfo: Object.fromEntries(Object.entries(formData.contactInfo).filter(([_, v]) => v !== "")),
        address: Object.fromEntries(Object.entries(formData.address).filter(([_, v]) => v !== "")),
        ...(isAccountingFirm ? { branchCode: formData.branchCode.trim() } : {}),
      };
      if (!isAccountingFirm) {
        // Not shown to other tenant types, so never sent by them.
        delete payload.branchCode;
        delete payload.whtProfile;
      }

      await accountingClientsAPI.update(id, payload);

      if (isOwner) {
        await releaseLock();
      }

      setOriginalData(formData);
      setSuccess("Saved ✅");
      setTimeout(() => setSuccess(""), 1200);
    } catch (e) {
      // accountingClientsAPI rethrows the server's message as a plain string.
      setError(typeof e === "string" ? e : e?.response?.data?.message || e?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };
  // CLAUDE.md: every mutation button goes through useGuardedMutation (blocks a
  // double submit; stamps the Idempotency-Key the PUT route dedupes on).
  const { mutate: runSave, isPending: saving } = useGuardedMutation(handleSave);

  const handleArchive = async () => {
    try {
      setLoading(true);
      setError("");
      // implement endpoint: POST /accounting-clients/:id/archive
      await accountingClientsAPI.archive(id);
      setMeta((m) => ({ ...m, status: "archived" }));
      setSuccess("Client archived");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to archive");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!meta.canDelete) return;
    const ok = window.confirm("Delete this client permanently? This cannot be undone.");
    if (!ok) return;

    try {
      setLoading(true);
      setError("");
      await accountingClientsAPI.delete(id);
      navigate("/client/accounting-clients");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (isOwner) {
      await releaseLock();
    }
    navigate("/client/accounting-clients");
  };

  const taxObligationsList = useMemo(
    () => [
      { key: "percentageTax", title: "Percentage Tax", subtitle: "2551Q" },
      { key: "vat", title: "VAT", subtitle: "2550Q" },
      { key: "withholdingTaxExpanded", title: "Withholding (Expanded)", subtitle: "2307" },
      { key: "withholdingTaxCompensation", title: "Withholding (Comp)", subtitle: "2316" },
      { key: "withholdingFinal", title: "Withholding (Final)", subtitle: "2306" },
      { key: "incomeTax", title: "Income Tax", subtitle: "1701/1702" },
      { key: "exciseTax", title: "Excise Tax", subtitle: "If applicable" },
    ],
    []
  );

  return (
    <Layout pageTitle="Accounting Client Details">
      <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gray-50 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">{formData.name || "Client"}</h1>
              <div className="text-sm text-gray-600 mt-1 flex gap-2 items-center">
                <span className={`px-2 py-0.5 rounded border text-xs ${meta.status === "archived" ? "border-gray-300 text-gray-500" : "border-teal-200 text-teal-700"}`}>
                  {meta.status === "archived" ? "Archived" : "Active"}
                </span>
                <span>Type: {formData.clientType}</span>
                <span>•</span>
                <span>Industry: {formData.industryTemplate}</span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Back
              </button>

              <button
                type="button"
                onClick={() => runSave()}
                className={`px-4 py-2 rounded-md text-sm text-white ${isDirty ? "bg-teal-600 hover:bg-teal-700" : "bg-gray-300 cursor-not-allowed"}`}
                disabled={loading || saving || !isDirty}
              >
                {loading || saving ? "Saving..." : "Save Changes"}
              </button>

              <button
                type="button"
                onClick={handleArchive}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                disabled={loading || meta.status === "archived"}
              >
                Archive
              </button>

              {meta.canDelete && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2 rounded-md text-sm text-white bg-red-600 hover:bg-red-700"
                  disabled={loading}
                >
                  Delete
                </button>
              )}
            </div>
          </div>

          {error && <div className="mt-4 bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded">{error}</div>}
          {success && <div className="mt-4 bg-green-100 border border-green-300 text-green-700 px-4 py-3 rounded">{success}</div>}

          {/* Tabs */}
          <div className="mt-4 bg-white rounded-lg border p-2 flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={`px-3 py-2 rounded-md text-sm border ${
                  activeTab === t.key ? "border-teal-300 bg-teal-50 text-teal-700" : "border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                }`}
                disabled={loading}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <RecordLockBanner isLocked={isLocked} lockedByName={lockedByName} lockedAt={lockedAt} />

        {/* Content */}
        <fieldset disabled={isLocked}>
        <div className="bg-white rounded-lg shadow-sm border p-6">
          {activeTab === "profile" && (
            <section>
              <h2 className="text-xl font-semibold mb-4">Profile</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name *">
                  <input className="w-full border rounded px-3 py-2" value={formData.name} onChange={(e) => handleInputChange("name", e.target.value)} />
                </Field>

                <Field label="Client Type *">
                  <select className="w-full border rounded px-3 py-2" value={formData.clientType} onChange={(e) => handleInputChange("clientType", e.target.value)}>
                    <option value="individual">Individual</option>
                    <option value="corporation">Corporation</option>
                    <option value="opc">OPC</option>
                    <option value="partnership">Partnership</option>
                  </select>
                </Field>

                <Field label="Industry Template">
                  <select className="w-full border rounded px-3 py-2" value={formData.industryTemplate} onChange={(e) => handleInputChange("industryTemplate", e.target.value)}>
                    <option value="general_service">General Service</option>
                    <option value="retail">Retail / Trading</option>
                  </select>
                </Field>

                <Field label="TIN *">
                  <input className="w-full border rounded px-3 py-2" value={formData.tin} onChange={(e) => handleInputChange("tin", e.target.value)} />
                </Field>

                <Field label="RDO Code *">
                  <input className="w-full border rounded px-3 py-2" value={formData.rdoCode} onChange={(e) => handleInputChange("rdoCode", e.target.value)} />
                </Field>

                {isAccountingFirm && (
                  <BranchCodeField value={formData.branchCode} onChange={(v) => handleInputChange("branchCode", v)} disabled={loading} />
                )}

                {["corporation", "opc", "partnership"].includes(formData.clientType) ? (
                  <Field label="SEC Registration Number *" className="md:col-span-2">
                    <input
                      className="w-full border rounded px-3 py-2"
                      value={formData.corporationDetails.secRegistrationNumber}
                      onChange={(e) => handleInputChange("corporationDetails.secRegistrationNumber", e.target.value)}
                    />
                  </Field>
                ) : null}
              </div>
            </section>
          )}

          {activeTab === "contact" && (
            <section className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">Contact</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Email">
                    <input className="w-full border rounded px-3 py-2" value={formData.contactInfo.email} onChange={(e) => handleInputChange("contactInfo.email", e.target.value)} />
                  </Field>
                  <Field label="Phone">
                    <input className="w-full border rounded px-3 py-2" value={formData.contactInfo.phone} onChange={(e) => handleInputChange("contactInfo.phone", e.target.value)} />
                  </Field>
                  <Field label="Mobile">
                    <input className="w-full border rounded px-3 py-2" value={formData.contactInfo.mobile} onChange={(e) => handleInputChange("contactInfo.mobile", e.target.value)} />
                  </Field>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold mb-4">Address</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Unit / Floor"><input className="w-full border rounded px-3 py-2" value={formData.address.unitFloor} onChange={(e)=>handleInputChange("address.unitFloor", e.target.value)} /></Field>
                  <Field label="Building"><input className="w-full border rounded px-3 py-2" value={formData.address.building} onChange={(e)=>handleInputChange("address.building", e.target.value)} /></Field>
                  <Field label="Street" className="md:col-span-2"><input className="w-full border rounded px-3 py-2" value={formData.address.street} onChange={(e)=>handleInputChange("address.street", e.target.value)} /></Field>
                  <Field label="Barangay"><input className="w-full border rounded px-3 py-2" value={formData.address.barangay} onChange={(e)=>handleInputChange("address.barangay", e.target.value)} /></Field>
                  <Field label="City / Municipality"><input className="w-full border rounded px-3 py-2" value={formData.address.cityMunicipality} onChange={(e)=>handleInputChange("address.cityMunicipality", e.target.value)} /></Field>
                  <Field label="Province"><input className="w-full border rounded px-3 py-2" value={formData.address.province} onChange={(e)=>handleInputChange("address.province", e.target.value)} /></Field>
                  <Field label="ZIP Code"><input className="w-full border rounded px-3 py-2" value={formData.address.zipCode} onChange={(e)=>handleInputChange("address.zipCode", e.target.value)} /></Field>
                </div>
              </div>
            </section>
          )}

          {activeTab === "tax" && (
            <section>
              <h2 className="text-xl font-semibold mb-4">Tax Obligations</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {taxObligationsList.map((ob) => {
                  const checked = !!formData.taxObligations[ob.key];
                  return (
                    <button
                      key={ob.key}
                      type="button"
                      onClick={() => handleTaxObligationChange(ob.key, !checked)}
                      className={`text-left border rounded-lg p-4 ${checked ? "border-teal-300 bg-teal-50" : "border-gray-200 hover:bg-gray-50"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold">{ob.title}</div>
                          <div className="text-xs text-gray-500">{ob.subtitle}</div>
                        </div>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => handleTaxObligationChange(ob.key, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 mt-1"
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Note: editing tax obligations here should update which Tax Modules appear in the sidebar for this client.
              </p>

              {isAccountingFirm && (
                <WhtProfileFields
                  value={formData.whtProfile}
                  taxObligations={formData.taxObligations}
                  onChange={(whtProfile) => setFormData((prev) => ({ ...prev, whtProfile }))}
                  disabled={loading || isLocked}
                />
              )}
            </section>
          )}

          {activeTab === "audit" && (
            <section>
              <h2 className="text-xl font-semibold mb-4">Audit</h2>
              <div className="text-sm text-gray-700 space-y-2">
                <div><span className="font-medium">Created:</span> {meta.createdAt || "—"} {meta.createdBy ? `by ${meta.createdBy}` : ""}</div>
                <div><span className="font-medium">Last Updated:</span> {meta.updatedAt || "—"} {meta.updatedBy ? `by ${meta.updatedBy}` : ""}</div>
                <div><span className="font-medium">Status:</span> {meta.status}</div>
              </div>
            </section>
          )}
        </div>
        </fieldset>
      </div>
    </Layout>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="block text-gray-700 text-sm font-bold mb-2">{label}</label>
    {children}
  </div>
);
